'use strict';

import type { APICriteria } from '@api/API/CoreAPI';
import { CommissionStatementCollection } from '../../Collections/CommissionStatement/CommissionStatementCollection';
import { CommissionStatement } from '../../Entities/CommissionStatement/CommissionStatement';
import type { CommissionStatementMapping } from '../../Entities/CommissionStatement/CommissionStatementMapping';
import { CommissionStatementResult } from '../../Entities/CommissionStatement/CommissionStatementResult';
import type { User } from '../../Entities/User/User';
import type {
  CommissionStatementModuleMode,
  CommissionStatementModuleName,
} from '../../Model/CommissionStatement/CommissionStatementModule';
import { Utilities } from '../../Utilities/Utilities';
import type { DeleteResponse } from '../CoreRepository';
import { CoreRepository } from '../CoreRepository';
import { CommissionStatementResultAPIOptions } from './CommissionStatementResultRepository';

/**
 *
 */
export enum CommissionStatementModule {
  AppleAndBackfill = 'apple_and_backfill',
}

/**
 * Valid statuses for CommissionStatement
 */
export enum CommissionStatementStatus {
  Completed = 'completed',
  Incomplete = 'incomplete',
}

/**
 * Complex type to be used in the SDK
 */
export type CommissionStatementSchema = {
  name?: string;
  file?: string;
  fileName?: string;
  sheet?: number;
  status?: CommissionStatementStatus;
  data?: any;
  dataFormatted?: any;
  dateTimeCreated?: Date;
  date?: Date;
  user?: User;
  commissionStatementMapping?: CommissionStatementMapping;
};

/**
 * Simple type for moving data between the SDK and the API
 */
export type CommissionStatementPayload = {
  name?: string;
  file?: string;
  fileName?: string;
  sheet?: number;
  status?: string;
  data?: any;
  dataFormatted?: any;
  dateTimeCreated?: string;
  date?: string;
  user?: number;
  commissionStatementMapping?: number;
};

/**
 * Format when returning a list of CommissionStatement entities from the API
 */
export type CommissionStatementCollectionPayload = {
  limit?: number;
  total?: number;
  offset?: number;
  data?: Array<CommissionStatementPayload>;
};

export type CommissionStatementCriteria = CommissionStatementPayload;

/**
 * Criteria for searching Commission Statements
 */
export type CommissionStatementAPICriteria = APICriteria<CommissionStatementCriteria>;

/**
 * Repository for CRUD operations within the context of a CommissionStatement type
 *
 * @author Matt Lowden <matt.lowden@medicareprocrm.com>
 */
export class CommissionStatementRepository extends CoreRepository {
  /**
   * Takes data supplied in the CommissionStatementSchema format and converts it to a simplified version
   * for use with the API
   *
   * @param {CommissionStatementSchema} data Data to be converted to a simplified format for usage with the API
   * @returns {CommissionStatementPayload} Simplified data structure for use with our API
   */
  protected processData(data: CommissionStatementSchema): CommissionStatementPayload {
    const payload: CommissionStatementPayload = {};

    if (data.hasOwnProperty('name')) {
      payload.name = data.name;
    }

    if (data.hasOwnProperty('sheet')) {
      payload.sheet = data.sheet;
    }

    if (data.hasOwnProperty('file')) {
      payload.file = data.file;
    }

    if (data.hasOwnProperty('fileName')) {
      payload.fileName = data.fileName;
    }

    if (data.hasOwnProperty('status')) {
      payload.status = data.status;
    }

    if (data.hasOwnProperty('data')) {
      payload.data = data.data;
    }

    if (data.hasOwnProperty('dataFormatted')) {
      payload.dataFormatted = data.dataFormatted;
    }

    if (data.user) {
      payload.user = data.user.getUserId();
    }

    if (data.commissionStatementMapping) {
      payload.commissionStatementMapping = data.commissionStatementMapping.getId();
    }

    if (data.hasOwnProperty('date')) {
      payload.date = data.date ? Utilities.dateToDateString(data.date) : null;
    }

    return payload;
  }

  /**
   * Gets a list of calendar statements
   *
   * @param {number} userId ID of whose statements are being requested
   * @param {{}} options Object containing additional query parameters
   * @returns {Promise<CommissionStatementCollection>} A promise containing the requested CommissionStatementCollection.
   */
  public getCommissionStatements(
    userId: number,
    options = {}
  ): Promise<CommissionStatementCollection> {
    return this.webAPI.getCommissionStatements(userId, options).then((response) => {
      const collection = new CommissionStatementCollection();

      response.data.forEach((collectionItemData) => {
        const collectionItem = new CommissionStatement();
        collectionItem.hydrate(collectionItemData);

        collection.addItem(collectionItem);
      });

      collection.setOffset(response.offset);
      collection.setLimit(response.limit);
      collection.setTotal(response.total);

      return collection;
    });
  }

  /**
   * Gets a single calendar statement
   *
   * @param {number} userId ID of User whose statement is being requested
   * @param {number} commissionStatementId ID of calendar statement being requested
   * @param {{}} options Object containing additional query parameters
   * @returns {Promise<CommissionStatement>} Promise containing requested CommissionStatement
   * @throws {Exception} If no matching statement exists or user isn't authenticated
   */
  public getCommissionStatement(
    userId: number,
    commissionStatementId: number,
    options = {}
  ): Promise<CommissionStatement> {
    return this.webAPI
      .getCommissionStatement(userId, commissionStatementId, options)
      .then((response) => {
        const item = new CommissionStatement();
        item.hydrate(response);

        return item;
      });
  }

  /**
   * Update a single CommissionStatement
   *
   * @param {number} userId ID of User whose statement is being requested
   * @param {number} commissionStatementId ID of calendar statement being requested
   * @param {CommissionStatementSchema} data Data to update
   * @returns {Promise<CommissionStatement>} Promise containing requested CommissionStatement
   * @throws {Exception} If no matching statement exists, the user isn't authenticated or suppled CommissionStatementSchema is invalid
   */
  public updateCommissionStatement(
    userId: number,
    commissionStatementId: number,
    data: CommissionStatementSchema
  ): Promise<CommissionStatement> {
    const payload = this.processData(data);

    return this.webAPI
      .updateCommissionStatement(userId, commissionStatementId, payload)
      .then((response) => {
        const item = new CommissionStatement();
        item.hydrate(response);

        return item;
      });
  }

  /**
   * Process a single CommissionStatement
   *
   * @param {number} userId ID of User whose statement is being requested
   * @param {number} commissionStatementId ID of calendar statement being requested
   * @returns {Promise<CommissionStatement>} Promise containing requested CommissionStatement
   * @throws {Exception} If no matching statement exists, the user isn't authenticated or suppled CommissionStatementSchema is invalid
   */
  public processCommissionStatement(
    userId: number,
    commissionStatementId: number
  ): Promise<CommissionStatement> {
    return this.webAPI
      .processCommissionStatement(userId, commissionStatementId)
      .then((response) => {
        const item = new CommissionStatement();
        item.hydrate(response);

        return item;
      });
  }

  /**
   * Transform a single CommissionStatement
   *
   * @param {number} userId ID of User whose statement is being transformed
   * @param {number} commissionStatementId ID of calendar statement being transformed
   * @returns {Promise<CommissionStatement>} Promise containing requested CommissionStatement
   * @throws {Exception} If no matching statement exists, the user isn't authenticated or suppled CommissionStatementSchema is invalid
   */
  public transformCommissionStatement(
    userId: number,
    commissionStatementId: number
  ): Promise<CommissionStatement> {
    return this.webAPI
      .transformCommissionStatement(userId, commissionStatementId)
      .then((response) => {
        const item = new CommissionStatement();
        item.hydrate(response);

        return item;
      });
  }

  /**
   * Create a single CommissionStatement
   *
   * @param {number} userId ID of User whose statement is being requested
   * @param {CommissionStatementSchema} data Data to create
   * @returns {Promise<CommissionStatement>} Promise containing requested CommissionStatement
   * @throws {Exception} The user isn't authenticated or suppled CommissionStatementSchema is invalid
   */
  public createCommissionStatement(
    userId: number,
    data: CommissionStatementSchema
  ): Promise<CommissionStatement> {
    const payload = this.processData(data);

    return this.webAPI.createCommissionStatement(userId, payload).then((response) => {
      const item = new CommissionStatement();
      item.hydrate(response);

      return item;
    });
  }

  /**
   * Delete a single CommissionStatement
   *
   * @param {number} userId ID of User whose statement is being deleted
   * @param {number} commissionStatementId ID of calendar statement being deleted
   * @returns {Promise<void>} Empty promise
   * @throws {Exception} The user isn't authenticated or supplied statement doesn't exist
   */
  public deleteCommissionStatement(
    userId: number,
    commissionStatementId: number
  ): Promise<DeleteResponse> {
    return this.webAPI.deleteCommissionStatement(userId, commissionStatementId).then((response) => {
      return response;
    });
  }

  /**
   * Delete a single CommissionStatement
   *
   * @param {number} userId ID of User whose statement is being deleted
   * @param {number} commissionStatementId ID of calendar statement being deleted
   * @returns {Promise<CommissionStatementResult>}
   * @throws {Exception} The user isn't authenticated or supplied statement doesn't exist
   */
  public runCommissionStatementModule(
    userId: number,
    commissionStatementId: number,
    module: CommissionStatementModuleName,
    mode: CommissionStatementModuleMode = undefined,
    options?: CommissionStatementResultAPIOptions
  ): Promise<CommissionStatementResult> {
    return this.webAPI
      .runCommissionStatementModule(userId, commissionStatementId, module, mode, options)
      .then((response) => {
        const item = new CommissionStatementResult();
        item.hydrate(response);

        return item;
      });
  }
}
